/**
 * Brain API client (Phase-Brain) — the typed surface chaos-ui calls to drive
 * the Second Brain. Mirrors apps/chaos-gateway/src/brain.ts response shapes
 * exactly. Cookie-auth via apiJson (credentials: 'include'), owner-scoped.
 *
 * ROUTES (all under /api/brain/*):
 *   POST /api/brain/ask              — query → multimodal answer + provenance.
 *   GET  /api/brain/library          — taxonomy (no type) OR items (with type).
 *   POST /api/brain/organize         — candidates | triage | propose | approve.
 *   GET  /api/brain/entity/:id       — entity page (mentioned-in + related).
 *   GET  /api/brain/entity/:id/graph — local, entity-anchored graph (depth 1-2).
 *
 * NOTHING here ever creates cards/boards — `approve` only records approval.
 */
import { apiJson } from './client';

// ── /ask ──────────────────────────────────────────────────────────────
export type AskScope = 'user_private' | 'project' | 'org' | 'all';
export type Modality = 'text' | 'image' | 'audio' | 'video';

/**
 * A single retrieved source the answer cites / lists. Mirrors the gateway's
 * brain-synth `SourceRef` on the wire EXACTLY (apps/chaos-gateway/src/lib/
 * brain-synth.ts). Fields that aren't on the wire (chunk_id/source_uri/
 * chunk_index/score) are intentionally absent — the UI must read `title`,
 * `deep_link`, and (for media) `media_url`.
 */
export interface BrainSource {
  n: number;
  source_id: string;
  source_kind: string;
  /** display title (note title / media caption). */
  title: string | null;
  /** deep link to the exact passage; for media this is the raw playable URL. */
  deep_link: string | null;
  modality: Modality;
  snippet: string;
  /** raw playable/presigned URL for media (image/audio/video); null for text. */
  media_url: string | null;
}

/** A claim in the synthesized answer, with the passage numbers backing it. */
export interface BrainClaim {
  text: string;
  citations: number[];
}

export interface AskResponse {
  query: string;
  scope: AskScope;
  answer: string;
  lang: string;
  synthesis_mode: 'extractive' | 'llm' | 'empty';
  claims: BrainClaim[];
  by_modality: Record<Modality, BrainSource[]>;
  matching_items: BrainSource[];
  retrieval: {
    embedded: boolean;
    reranked: boolean;
    arms: string[];
    media_arm: 'off' | 'empty' | 'merged';
    entity_arm: 'off' | 'empty' | 'matched';
    entity_anchors: unknown[];
  };
}

export interface AskBody {
  query: string;
  k?: number;
  rerank?: boolean;
  scope?: AskScope;
  project_id?: string;
  lang?: 'russian' | 'english' | 'simple';
}

// ── /library ──────────────────────────────────────────────────────────
export interface TaxonomyType {
  type: string;
  label: string;
  count: number;
  group: 'content' | 'entity';
}

export interface LibraryTaxonomy {
  content_types: TaxonomyType[];
  entity_types: TaxonomyType[];
  unsorted_count: number;
}

export type LibraryItemKind = 'source' | 'media' | 'entity' | 'unsorted';

/** Source/unsorted library item (notes/docs/messages/…). */
export interface SourceItem {
  id: string;
  source_kind: string;
  title: string | null;
  source_uri: string | null;
  folder?: string | null;
  tags?: string[] | null;
  chunk_count?: number | null;
  ingest_status?: string | null;
  source_updated_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

/** Media library item (images/audio/video) — gallery view presigns `url`. */
export interface MediaItem {
  id: string;
  media_kind: 'image' | 'audio' | 'video';
  caption: string | null;
  mime_type: string | null;
  byte_size: number | null;
  ingest_status: string;
  created_at: string;
  url: string | null;
}

/** Entity library item (people/projects/organizations + future kinds). */
export interface EntityListItem {
  id: string;
  kind: string;
  name: string;
  summary: string | null;
  mention_count: number;
  aliases?: string[] | null;
  last_extracted_at?: string | null;
}

export interface LibraryItemsResponse {
  type: string;
  item_kind: LibraryItemKind;
  view: 'list' | 'gallery';
  limit: number;
  offset: number;
  total: number;
  count: number;
  items: (SourceItem | MediaItem | EntityListItem)[];
}

export interface LibraryQueryParams {
  type?: string;
  q?: string;
  limit?: number;
  offset?: number;
  view?: 'list' | 'gallery';
}

// ── /source/:id (the reader content endpoint, R4/§9) ──────────────────
/** Chunk-anchored readable content for a source. `chunks` power ?chunk=N. */
export interface BrainSourceContent {
  kind: 'markdown' | 'text';
  /** full joined body (copy + fallback render). */
  body: string;
  truncated: boolean;
  /** ordered chunks so the reader can wrap each in <section id="chunk-N">. */
  chunks: { index: number; text: string }[];
}

/** Playable media descriptor for an image/audio/video reader row. */
export interface BrainSourceMedia {
  media_kind: 'image' | 'audio' | 'video';
  /** presigned URL; null when storage isn't configured / presign failed. */
  url: string | null;
  mime_type: string | null;
  byte_size: number | null;
}

/** An entity extracted FROM this source (the reader's Mentions rail). */
export interface BrainSourceMention {
  entity_id: string;
  kind: string;
  name: string;
  relation: string | null;
  trust: TrustTier;
}

/**
 * GET /api/brain/source/:id — everything the reader needs to OPEN a library row
 * in-app. 404 ⇒ feature off (gateway BRAIN_SOURCE_READ_ENABLED) or unknown id →
 * the UI degrades to today's deep-link behavior. `source.id` may be a note/doc
 * (content set) OR a media row (media set).
 */
export interface BrainSourceResponse {
  source: SourceItem;
  content: BrainSourceContent | null;
  media: BrainSourceMedia | null;
  origin: CandidateOrigin;
  mentions: BrainSourceMention[];
  chunk_count: number;
}

// ── /organize ─────────────────────────────────────────────────────────
/**
 * An openable deep-link descriptor for a candidate's origin. The UI maps each
 * variant to a concrete destination; `preview` means "no live external target —
 * open the item's content preview in place" (honest fallback for virtual URIs).
 * Mirrors the gateway `OpenLink` discriminated union exactly.
 */
export type OpenLink =
  | { kind: 'project'; project_id: string; project_name: string | null }
  | { kind: 'explorer'; path: string }
  | { kind: 'external'; url: string }
  | { kind: 'preview' };

/** WHERE a candidate came from — human label + openable descriptor. */
export interface CandidateOrigin {
  label: string;
  detail: string | null;
  open: OpenLink;
}

/**
 * A STRUCTURED + ATTRIBUTED + OPENABLE organize candidate. Never a bare title:
 * carries kind (extensible taxonomy), origin (where it came from + how to open
 * it), a real content preview, and created/updated dates.
 */
export interface OrganizeCandidate {
  id: string;
  title: string | null;
  /** extensible content type (notes/docs/images/… or a raw source_kind). */
  kind: string;
  /** raw source_kind — drives the type icon. */
  source_kind: string;
  origin: CandidateOrigin;
  /** a real content snippet (not the title); null when there's no body. */
  preview: string | null;
  chunk_count: number | null;
  created_at: string;
  updated_at: string;
}

/** A structured sample item inside a triage batch (not a bare title). */
export interface OrganizeBatchSample {
  id: string;
  title: string | null;
  kind: string;
  source_kind: string;
  origin: CandidateOrigin;
  preview: string | null;
}

/**
 * An AI-triage batch: a clear label + "what's in it + where it'll go"
 * (destination) + count + structured sample items.
 */
export interface OrganizeBatch {
  batch_key: string;
  kind: string;
  /** "134 workspace files → Project: CHAOS" — the one-glance batch summary. */
  label: string;
  /** human destination, e.g. "Project: CHAOS", "Folder: outputs", "Unsorted". */
  destination: string;
  count: number;
  sample_items: OrganizeBatchSample[];
  source_ids: string[];
  /** back-compat titles-only list (older clients). */
  sample_titles: string[];
}

export interface ProposalCluster {
  id: string;
  cluster_key: number;
  proposed_project: string | null;
  proposed_board: string | null;
  description: string | null;
  proposed_project_id: string | null;
  is_seed_anchor: boolean;
  member_count: number;
  confidence: number | null;
  keywords: string[] | null;
  sample_titles: string[] | null;
}

export interface ProposalRoute {
  id: string;
  source_id: string;
  cluster_id: string | null;
  proposed_project: string | null;
  proposed_board: string | null;
  classification: string;
  confidence: number | null;
  needs_review: boolean;
  rationale: string | null;
  source_uri: string | null;
  source_title: string | null;
  dedup_group_id: string | null;
}

export interface ProposalDedupRow {
  group_id: string;
  source_id: string;
  kind: string;
  similarity: number | null;
  is_representative: boolean;
  source_uri: string | null;
  source_title: string | null;
}

export interface ProposeResponse {
  action: 'propose';
  run_id: string;
  status: 'proposed';
  summary: {
    sources: number;
    clusters: number;
    chosen_k: number;
    silhouette: number;
    actionable: number;
    reference: number;
    needs_review: number;
    dedup_groups: number;
  };
  clusters: ProposalCluster[];
  routes: ProposalRoute[];
  dedup_groups: ProposalDedupRow[];
  /** present (with error) when there's nothing embedded to organize. */
  error?: string;
  message?: string;
}

export interface ApproveResponse {
  action: 'approve';
  run_id: string;
  status: 'approved';
  scope: 'all' | 'per_group';
  approved_cluster_ids?: string[];
}

// ── /entity/:id ───────────────────────────────────────────────────────
export type TrustTier = 'trusted' | 'inferred' | 'review';

export interface EntityMention {
  source_id: string;
  source_title: string | null;
  source_kind: string;
  chunk_index: number | null;
  relation: string | null;
  trust: TrustTier;
  confidence: number | null;
  surrounding: string;
  deep_link: string | null;
}

export interface RelatedEntity {
  entity_id: string;
  kind: string;
  name: string;
  co_mentions: number;
  trust: TrustTier;
}

export interface EntityPageResponse {
  entity: {
    id: string;
    kind: string;
    name: string;
    summary: string | null;
    aliases: string[];
    mention_count: number;
    last_extracted_at: string | null;
  };
  mentioned_in: EntityMention[];
  related_entities: RelatedEntity[];
  graph_endpoint: string;
}

// ── /entity/:id/graph (LOCAL, entity-anchored) ─────────────────────────
export interface GraphNode {
  id: string;
  kind: string;
  name: string;
  depth: number;
}

export interface GraphEdge {
  source: string;
  target: string;
  weight: number;
  trust: TrustTier;
}

export interface LocalGraphResponse {
  anchor: { id: string; kind: string; name: string };
  depth: number;
  node_cap: number;
  nodes: GraphNode[];
  edges: GraphEdge[];
  scope: 'local_entity_anchored';
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
}

// ── Phase-C: KB Promotion Pipeline (the "Sorter inbox") ───────────────
// Mirrors apps/chaos-gateway/src/kb-promotion.ts. Flag-gated server-side
// (PROMOTION_PIPELINE_ENABLED, default OFF) — when OFF every route 404s, so the
// UI must treat a 404 here as "feature unavailable" and hide the surface.
export type PromotionProposer = 'agent' | 'conductor' | 'user';
export type KbClassification = 'general' | 'organization_specific';
export type PromotionProposalStatus = 'pending' | 'approved' | 'rejected' | 'applied';

export interface PromotionTargetGrant {
  granteeType: 'organization' | 'department' | 'role';
  granteeId: string;
}
export interface PromotionTarget {
  collectionId?: string | null;
  collectionName?: string | null;
  grants?: PromotionTargetGrant[];
}

/** A proposal row as returned by GET /api/brain/promotions (joined w/ source + org). */
export interface PromotionProposal {
  id: string;
  source_id: string;
  account_id: string;
  origin_org_id: string | null;
  proposed_by: PromotionProposer | null;
  proposed_by_user_id: string | null;
  proposed_by_role_id: string | null;
  proposed_classification: KbClassification | null;
  proposed_target: PromotionTarget;
  rationale: string | null;
  sanitization_flags: Array<{ kind: string; hint: string }>;
  status: PromotionProposalStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  applied_at: string | null;
  created_at: string;
  // joined display fields
  source_title: string | null;
  source_kind: string;
  source_classification: KbClassification | null;
  source_promotion_status: string;
  origin_org_slug: string | null;
  origin_org_name: string | null;
}

export interface PromotionsListResponse {
  account_id: string;
  is_account_owner: boolean;
  status: PromotionProposalStatus | 'all';
  proposals: PromotionProposal[];
  count: number;
}

export interface CreatePromotionBody {
  sourceId: string;
  proposedClassification?: KbClassification;
  proposedTarget?: PromotionTarget;
  rationale?: string | null;
  originOrgId?: string | null;
}

export type PromotionDecision = 'promote' | 'keep_local' | 'discard';

export const brainApi = {
  ask(body: AskBody): Promise<AskResponse> {
    return apiJson<AskResponse>('/brain/ask', { method: 'POST', json: body });
  },

  /** No `type` → taxonomy (tabs + counts). */
  taxonomy(): Promise<{ taxonomy: LibraryTaxonomy }> {
    return apiJson<{ taxonomy: LibraryTaxonomy }>('/brain/library');
  },

  /** With `type` → that type's items. */
  libraryItems(params: LibraryQueryParams): Promise<LibraryItemsResponse> {
    return apiJson<LibraryItemsResponse>(
      `/brain/library${buildQuery({
        type: params.type,
        q: params.q,
        limit: params.limit,
        offset: params.offset,
        view: params.view,
      })}`,
    );
  },

  organizeCandidates(): Promise<{ action: 'candidates'; candidates: OrganizeCandidate[]; count: number }> {
    return apiJson('/brain/organize', { method: 'POST', json: { action: 'candidates' } });
  },

  organizeTriage(sourceIds?: string[]): Promise<{ action: 'triage'; batches: OrganizeBatch[]; total_sources: number }> {
    return apiJson('/brain/organize', {
      method: 'POST',
      json: { action: 'triage', source_ids: sourceIds },
    });
  },

  organizePropose(notes?: string): Promise<ProposeResponse> {
    return apiJson<ProposeResponse>('/brain/organize', {
      method: 'POST',
      json: { action: 'propose', notes },
    });
  },

  /** Approve-all (no cluster_ids) or per-group (cluster_ids). Records approval only. */
  organizeApprove(runId: string, clusterIds?: string[]): Promise<ApproveResponse> {
    return apiJson<ApproveResponse>('/brain/organize', {
      method: 'POST',
      json: { action: 'approve', run_id: runId, cluster_ids: clusterIds },
    });
  },

  /** Reader content for a library row (note/doc/media). 404 ⇒ feature off. */
  source(id: string): Promise<BrainSourceResponse> {
    return apiJson<BrainSourceResponse>(`/brain/source/${id}`);
  },

  entity(id: string): Promise<EntityPageResponse> {
    return apiJson<EntityPageResponse>(`/brain/entity/${id}`);
  },

  entityGraph(id: string, depth = 1): Promise<LocalGraphResponse> {
    return apiJson<LocalGraphResponse>(`/brain/entity/${id}/graph${buildQuery({ depth })}`);
  },

  // ── Phase-C: promotion pipeline (Sorter inbox) ──────────────────────
  /** List promotion proposals for the in-scope account. 404 ⇒ feature off. */
  promotionsList(status: PromotionProposalStatus | 'all' = 'pending'): Promise<PromotionsListResponse> {
    return apiJson<PromotionsListResponse>(`/brain/promotions${buildQuery({ status })}`);
  },

  /** Propose promoting a source (user surface). */
  promotionsCreate(body: CreatePromotionBody): Promise<{ proposal: PromotionProposal }> {
    return apiJson<{ proposal: PromotionProposal }>('/brain/promotions', { method: 'POST', json: body });
  },

  /** Founder decision: promote (apply Phase-B), keep_local, or discard. */
  promotionsDecide(
    proposalId: string,
    decision: PromotionDecision,
    target?: PromotionTarget,
  ): Promise<{ proposal_id: string; decision: PromotionDecision; status: string; collection_id?: string; grants?: number }> {
    return apiJson('/brain/promotions/decide', {
      method: 'POST',
      json: { proposalId, decision, target },
    });
  },
};

export const brainKeys = {
  all: ['brain'] as const,
  taxonomy: () => ['brain', 'taxonomy'] as const,
  library: (params: LibraryQueryParams) => ['brain', 'library', params] as const,
  source: (id: string) => ['brain', 'source', id] as const,
  candidates: () => ['brain', 'organize', 'candidates'] as const,
  entity: (id: string) => ['brain', 'entity', id] as const,
  entityGraph: (id: string, depth: number) => ['brain', 'entity', id, 'graph', depth] as const,
  promotions: (status: string) => ['brain', 'promotions', status] as const,
};
